import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { add } from 'date-fns';

const createToken = (tenant, server, { invited, connected, expired, email } = {}) => {
  const connectionRequest = server.create('tenant-connection-request', {
    tenant,
    token: faker.string.uuid(),
    expiresAt: add(new Date(), { days: expired ? 7 : 0 }),
    connectedAt: connected ? new Date() : null,
  });

  if (invited || expired || connected) {
    connectionRequest.update({
      connectionRequestInvites: [
        server.create('connection-request-invite', {
          email: email ?? faker.internet.email(),
          createdAt: new Date(),
          updatedAt: new Date(),
        }),
      ],
    });
  }
};

export default Factory.extend({
  name: () => faker.company.name(),
  leaseExpirationDate: () => faker.date.future(),
  annualizedRent: () => faker.number.int({ min: 1000, max: 10000 }) * 100,
  annualizedRentCurrencyCode: 'USD',
  expectedEmployeesCount: () => faker.number.int({ min: 10, max: 100 }),

  afterCreate(tenant, server) {
    if (tenant.property) {
      tenant.update({
        propertyName: tenant.property.name,
        propertyCompanyName: tenant.property.company.name,
      });
    }

    tenant.update({
      tenantPermissionsConfiguration: server.create('tenant-permissions-configuration', {
        employeesRead: true,
      }),
    });
  },

  asInvited: trait({
    status: 'invited',
    afterCreate: (tenant, server) => createToken(tenant, server, { invited: true, email: tenant.email }),
  }),

  employeesReadDisabled: trait({
    afterCreate(tenant) {
      tenant.tenantPermissionsConfiguration.update({
        employeesRead: false,
      });
    },
  }),

  asInviteExpired: trait({
    status: 'invite_expired',
    afterCreate: (tenant, server) => createToken(tenant, server, { invited: true, expired: true, email: tenant.email }),
  }),

  asNotInvited: trait({
    status: 'not_invited',
    afterCreate: (tenant, server) => createToken(tenant, server),
  }),

  asPending: trait({
    status: 'pending',
    afterCreate: (tenant, server) => createToken(tenant, server),
  }),

  asDisconnected: trait({
    status: 'disconnected',
    afterCreate: (tenant, server) => createToken(tenant, server, { invited: true, connected: true }),
  }),

  asInactive: trait({
    status: 'inactive',
    afterCreate: (tenant, server) => createToken(tenant, server, { invited: true, connected: true }),
  }),

  asConnected: trait({
    status: 'connected',
    afterCreate: (tenant, server) => createToken(tenant, server, { invited: true, connected: true }),
  }),

  withLogo: trait({
    afterCreate(tenant) {
      tenant.update({
        logo: faker.helpers.arrayElement([
          '/assets/images/logo.svg',
          '/assets/images/rooms/google-logo.svg',
          '/assets/images/rooms/microsoft-logo.svg',
        ]),
      });
    },
  }),

  withSuites: trait({
    afterCreate(tenant, server) {
      tenant.update({
        suites: server.createList('zone', 3, 'asSuite', { parent: tenant.property }),
      });
    },
  }),

  withInvites: trait({
    afterCreate(tenant, server) {
      server.createList('invite', 2, 'fromTenant', {
        tenantId: tenant.id,
      });
      server.create('invite', 'fromTenant', 'asCheckedIn', {
        tenantId: tenant.id,
      });
      server.create('invite', 'fromTenant', 'asCheckedIn', 'withSharedNotes', 'withPhoto', {
        tenantId: tenant.id,
      });
    },
  }),
});
